import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Search,
  ArrowLeft,
  Delete,
  X
} from 'lucide-react';
import toast from 'react-hot-toast';
import { documentsApi } from '../../utils/api';

export default function SeleccionarDocumento() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tipo = searchParams.get('tipo') || 'facturas';
  const esCotizacion = tipo === 'cotizaciones';

  const [documentos, setDocumentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Load documents
  const cargarDocumentos = async () => {
    setLoading(true);
    try {
      const params = {
        status: 'draft',
        limit: 100
      };

      // Add search if present
      if (searchTerm && searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      // Add tipo filter for cotizaciones
      if (esCotizacion) {
        params.tipo = 'COT';
      }

      const response = await documentsApi.list(params);
      let docs = response.data?.data || [];

      // Filter out COT for facturas view
      if (!esCotizacion) {
        docs = docs.filter(d => d.tipoDocumento !== 'COT');
      }

      setDocumentos(docs);
    } catch (error) {
      console.error('Error loading documents:', error);
      toast.error('Error al cargar documentos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarDocumentos();
    setSelectedIds([]);
  }, [tipo]);

  // Search with debounce
  const handleBuscar = () => {
    cargarDocumentos();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleBuscar();
    }
  };

  const limpiarBusqueda = () => {
    setSearchTerm('');
    setTimeout(() => cargarDocumentos(), 100);
  };

  const seleccionarDocumento = (doc) => {
    navigate(`/facturacion/nueva?cargar=${doc.id}`);
  };

  const toggleSeleccion = (id, e) => {
    e.stopPropagation();
    setSelectedIds(prev =>
      prev.includes(id)
        ? prev.filter(x => x !== id)
        : [...prev, id]
    );
  };

  const handleEliminar = async () => {
    if (selectedIds.length === 0) return;

    try {
      for (const id of selectedIds) {
        await documentsApi.delete(id);
      }
      toast.success(`${selectedIds.length} documento(s) eliminado(s)`);
      setSelectedIds([]);
      setShowDeleteConfirm(false);
      cargarDocumentos();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Error al eliminar documentos');
    }
  };

  const formatCurrency = (amount, moneda = 'CRC') => {
    const symbol = moneda === 'USD' ? '$' : '₡';
    return `${symbol}${parseFloat(amount || 0).toLocaleString('es-CR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('es-CR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return '-';
    }
  };

  return (
    <div className="h-full flex flex-col bg-gray-100">
      {/* Top toolbar */}
      <div className="bg-white border-b border-gray-300 px-4 py-2 flex items-center gap-4">
        <button
          onClick={() => {
            if (selectedIds.length > 0) {
              setShowDeleteConfirm(true);
            } else {
              toast.error('Seleccione al menos un documento');
            }
          }}
          className="flex flex-col items-center px-3 py-1 hover:bg-red-50 rounded transition-colors text-gray-600 hover:text-red-600"
        >
          <Delete className="h-5 w-5 text-red-500" />
          <span className="text-xs mt-0.5">Eliminar</span>
        </button>

        <div className="text-xs text-gray-500">
          {esCotizacion ? 'Recuperar Cotizaciones' : 'Recuperar Facturas'}
        </div>
      </div>

      {/* Header */}
      <div className="bg-white px-6 py-4 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/facturacion/nueva')}
            className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-blue-600" />
          </button>
          <h1 className="text-xl font-semibold text-blue-700">
            {esCotizacion ? 'Cotizaciones Guardadas' : 'Facturas Guardadas'}
          </h1>
        </div>
      </div>

      {/* Search bar */}
      <div className="bg-white px-6 py-3 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <div className="flex-1 relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Buscar por consecutivo o cliente..."
              className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <button
            onClick={handleBuscar}
            className="px-4 py-1.5 bg-gray-100 border border-gray-300 rounded text-sm hover:bg-gray-200 transition-colors"
          >
            Buscar
          </button>
          <button
            onClick={limpiarBusqueda}
            className="px-4 py-1.5 bg-gray-100 border border-gray-300 rounded text-sm hover:bg-gray-200 transition-colors"
          >
            Borrar
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto px-6 py-4">
        <div className="bg-white border border-gray-300 rounded overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">
              <div className="animate-spin h-6 w-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>
              Cargando...
            </div>
          ) : documentos.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No hay {esCotizacion ? 'cotizaciones' : 'facturas'} guardadas
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-blue-50 border-b border-gray-300">
                  <th className="w-10 px-2 py-2">
                    <input
                      type="checkbox"
                      checked={selectedIds.length === documentos.length && documentos.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedIds(documentos.map(d => d.id));
                        } else {
                          setSelectedIds([]);
                        }
                      }}
                      className="rounded border-gray-300"
                    />
                  </th>
                  <th className="text-left px-3 py-2 font-medium text-gray-700 border-r border-gray-200">
                    Código {esCotizacion ? 'Cotización' : 'Factura'}
                  </th>
                  <th className="text-left px-3 py-2 font-medium text-gray-700 border-r border-gray-200">
                    Fecha
                  </th>
                  <th className="text-left px-3 py-2 font-medium text-gray-700 border-r border-gray-200">
                    Cliente
                  </th>
                  <th className="text-right px-3 py-2 font-medium text-gray-700">
                    Total Nominal
                  </th>
                </tr>
              </thead>
              <tbody>
                {documentos.map((doc, idx) => (
                  <tr
                    key={doc.id}
                    onClick={() => seleccionarDocumento(doc)}
                    className={`cursor-pointer border-b border-gray-100 transition-colors ${
                      selectedIds.includes(doc.id)
                        ? 'bg-blue-100'
                        : idx % 2 === 0 ? 'bg-white hover:bg-blue-50' : 'bg-gray-50 hover:bg-blue-50'
                    }`}
                  >
                    <td className="px-2 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(doc.id)}
                        onChange={(e) => toggleSeleccion(doc.id, e)}
                        className="rounded border-gray-300"
                      />
                    </td>
                    <td className="px-3 py-2 font-mono text-blue-600 border-r border-gray-100">
                      {doc.consecutivo || doc.id}
                    </td>
                    <td className="px-3 py-2 border-r border-gray-100">
                      {formatDate(doc.fechaEmision || doc.createdAt)}
                    </td>
                    <td className="px-3 py-2 border-r border-gray-100">
                      {doc.receptorNombre || 'Sin cliente asignado'}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">
                      {formatCurrency(doc.totales?.totalComprobante, doc.codigoMoneda)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Selection info */}
        {selectedIds.length > 0 && (
          <div className="mt-2 text-sm text-gray-600">
            {selectedIds.length} documento(s) seleccionado(s)
          </div>
        )}
      </div>

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <Delete className="h-5 w-5 text-red-500" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">
                Eliminar {esCotizacion ? 'Cotizaciones' : 'Facturas'}
              </h2>
            </div>
            <p className="text-gray-600 mb-6">
              ¿Está seguro que desea eliminar {selectedIds.length} {esCotizacion ? 'cotización(es)' : 'factura(s)'} guardada(s)?
              Esta acción no se puede deshacer.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 border-2 border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleEliminar}
                className="px-4 py-2 border-2 border-red-600 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
